import { useState, useCallback, useRef } from 'react';
import { apiService, type Document as ApiDocument } from '../services/api';
import logger from '../utils/simpleLogger';

const API_BASE = (
  import.meta.env.VITE_API_BASE_URL || (import.meta.env.DEV ? 'http://localhost:8001/api' : '/api')
).replace(/\/$/, '');

interface UseDriveUploadProps {
  // projectId removed - project system eliminated
  onDocumentUpload?: (document: ApiDocument) => void;
  loadPrivateDocuments: () => Promise<void>;
  loadSharedDocuments: () => Promise<void>;
}

export const useDriveUpload = ({
  onDocumentUpload,
  loadPrivateDocuments,
  loadSharedDocuments,
}: UseDriveUploadProps) => {
  const [isDragOver, setIsDragOver] = useState(false);
  const [isUploading, setIsUploading] = useState(false);
  const [uploadProgress, setUploadProgress] = useState<{
    isUploading: boolean;
    progress: number;
    status: string;
    filename: string;
  } | null>(null);
  const [uploadError, setUploadError] = useState<string | null>(null);

  const fileInputRef = useRef<HTMLInputElement>(null);
  const fileInputSharedRef = useRef<HTMLInputElement>(null);

  // Setup SSE monitoring for document processing
  const setupProcessingMonitor = useCallback(
    (documentId: string, isShared: boolean) => {
      const token = localStorage.getItem('access_token');
      if (!token) {
        logger.warning('⚠️ [DriveUpload] No access token found for SSE connection');
        return;
      }

      logger.info('📡 [DriveUpload] Connecting to SSE stream', { documentId, isShared });

      const eventSource = new EventSource(
        `${API_BASE}/documents/${documentId}/status-stream?token=${token}`
      );

      eventSource.addEventListener('status_update', async (event: MessageEvent) => {
        const data = JSON.parse(event.data);
        logger.info('📡 [DriveUpload] Status update received', {
          documentId,
          status: data.processing_status,
        });

        // Reload documents to reflect updated status
        if (isShared) {
          await loadSharedDocuments();
        } else {
          await Promise.all([loadPrivateDocuments(), loadSharedDocuments()]);
        }
      });

      eventSource.addEventListener('processing_complete', async (event: MessageEvent) => {
        const data = JSON.parse(event.data);
        logger.info('✅ [DriveUpload] Processing complete', {
          documentId,
          status: data.processing_status,
        });

        // Final reload to show completed status
        if (isShared) {
          await loadSharedDocuments();
        } else {
          await Promise.all([loadPrivateDocuments(), loadSharedDocuments()]);
        }

        // Close connection
        eventSource.close();
      });

      eventSource.addEventListener('error', (event: Event) => {
        logger.error('❌ [DriveUpload] SSE error', { documentId, event });
        eventSource.close();
      });

      eventSource.addEventListener('timeout', async (event: MessageEvent) => {
        logger.warning('⏱️ [DriveUpload] SSE timeout', { documentId });
        eventSource.close();

        // Final reload after timeout
        if (isShared) {
          await loadSharedDocuments();
        } else {
          await Promise.all([loadPrivateDocuments(), loadSharedDocuments()]);
        }
      });
    },
    [loadPrivateDocuments, loadSharedDocuments]
  );

  // Unified function to upload files (private or shared)
  const handleFileUpload = useCallback(
    async (files: File[], isShared: boolean = false) => {
      logger.info('📄 [DriveUpload] handleFileUpload() called', {
        numberOfFiles: files.length,
        target: isShared ? 'shared' : 'private',
        // projectId removed
      });
      // Validation removed - project system eliminated
      // Private documents are always allowed (user is authenticated via JWT)

      setIsUploading(true);
      setUploadError(null);

      const results = {
        success: 0,
        failed: 0,
        errors: [] as string[],
      };

      try {
        for (const file of files) {
          try {
            logger.info('📄 [DriveUpload] Uploading file', {
              fileName: file.name,
              fileSize: file.size,
              target: isShared ? 'shared' : 'private',
            });
            setUploadProgress({
              isUploading: true,
              progress: 0,
              status: isShared
                ? 'Subiendo archivo a biblioteca compartida...'
                : 'Subiendo archivo...',
              filename: file.name,
            });

            let response;
            if (isShared) {
              // Shared upload
              const formData = new FormData();
              formData.append('file', file);
              response = await apiService.uploadAdminDocument(formData);
            } else {
              // Private upload
              response = await apiService.uploadDocument(file);
            }

            // Verify response
            // uploadDocument returns { document, message }, uploadAdminDocument returns { success, document?, message? }
            const hasDocument = 'document' in response && response.document;
            const isSuccess = 'success' in response ? response.success : hasDocument;

            if (!isSuccess || !hasDocument) {
              let errorMsg: string = 'Error al subir archivo';
              if ('error' in response && typeof response.error === 'string') {
                errorMsg = response.error;
              } else if ('message' in response && typeof response.message === 'string') {
                errorMsg = response.message;
              }
              throw new Error(errorMsg);
            }

            // TypeScript guard: we know document exists at this point
            const uploadedDocument = 'document' in response ? response.document : undefined;
            if (!uploadedDocument) {
              throw new Error('Documento no disponible después de la subida');
            }

            setUploadProgress({
              isUploading: true,
              progress: 100,
              status: isShared
                ? 'Archivo subido exitosamente a biblioteca compartida'
                : 'Archivo subido exitosamente',
              filename: file.name,
            });

            // Reload lists according to type
            if (isShared) {
              // Only reload shared list
              await loadSharedDocuments();
            } else {
              await Promise.all([loadPrivateDocuments(), loadSharedDocuments()]);
            }

            // Notify parent component
            if (onDocumentUpload) {
              onDocumentUpload(uploadedDocument);
            }
            logger.info('✅ [DriveUpload] File uploaded successfully', {
              documentId: uploadedDocument.id,
              fileName: file.name,
            });

            // Connect to SSE stream to monitor processing status
            setupProcessingMonitor(uploadedDocument.id, isShared);

            results.success++;
          } catch (error) {
            // Handle individual file error - continue with next files
            const errorMessage =
              error instanceof Error ? error.message : 'Error desconocido al subir archivo';
            const isDuplicate =
              errorMessage.includes('Duplicate file') || errorMessage.includes('already exists');

            results.failed++;
            results.errors.push(`${file.name}: ${errorMessage}`);

            if (isDuplicate) {
              logger.info('ℹ️ [DriveUpload] Duplicate file skipped', {
                fileName: file.name,
                error: errorMessage,
              });
            } else {
              logger.error('❌ [DriveUpload] File upload failed', {
                fileName: file.name,
                error: errorMessage,
              });
            }
          }
        }

        // Log summary
        logger.info('📄 [DriveUpload] All files processed', {
          totalFiles: files.length,
          success: results.success,
          failed: results.failed,
          isShared,
        });

        // Set error message if any files failed
        if (results.failed > 0) {
          const errorSummary =
            results.failed === 1
              ? `1 archivo no se pudo subir: ${results.errors[0]}`
              : `${results.failed} archivos no se pudieron subir. ${results.errors
                  .slice(0, 2)
                  .join('; ')}${results.errors.length > 2 ? '...' : ''}`;
          setUploadError(errorSummary);
        }
      } catch (error) {
        // This catch is for unexpected errors outside the loop
        logger.error('❌ [DriveUpload] handleFileUpload unexpected error', error);
        setUploadError(error instanceof Error ? error.message : 'Error al subir archivos');
      } finally {
        setIsUploading(false);
        setUploadProgress(null);
      }
    },
    [loadPrivateDocuments, loadSharedDocuments, onDocumentUpload, setupProcessingMonitor]
  );

  // Drag & drop handlers
  const handleFileDragOver = useCallback((e: React.DragEvent) => {
    e.preventDefault();
    setIsDragOver(true);
  }, []);

  const handleFileDragLeave = useCallback((e: React.DragEvent) => {
    e.preventDefault();
    setIsDragOver(false);
  }, []);

  const handleFileDrop = useCallback(
    (e: React.DragEvent) => {
      e.preventDefault();
      setIsDragOver(false);
      const files = Array.from(e.dataTransfer.files);
      if (files.length > 0) {
        handleFileUpload(files, false); // Always private in drag & drop
      }
    },
    [handleFileUpload]
  );

  // File selection handlers
  const handleFileSelect = useCallback(() => {
    fileInputRef.current?.click();
  }, []);

  const handleFileSelectShared = useCallback(() => {
    fileInputSharedRef.current?.click();
  }, []);

  // Unified handler for input change
  const handleFileInputChange = useCallback(
    (e: React.ChangeEvent<HTMLInputElement>, isShared: boolean = false) => {
      const files = Array.from(e.target.files || []);
      if (files.length > 0) {
        handleFileUpload(files, isShared);
        // Reset input to allow uploading the same file again
        e.target.value = '';
      }
    },
    [handleFileUpload]
  );

  return {
    isDragOver,
    isUploading,
    uploadProgress,
    uploadError,
    fileInputRef,
    fileInputSharedRef,
    handleFileUpload,
    handleFileDragOver,
    handleFileDragLeave,
    handleFileDrop,
    handleFileSelect,
    handleFileSelectShared,
    handleFileInputChange,
    setUploadError,
  };
};
